import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, PROCESS_TIMES, loadProcessTimes } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, ArrowLeft } from "lucide-react";
import { DeliveryPicker } from "@/components/ProductOptions";
import { OrderItemsEditor, newItem, itemPayload } from "@/components/OrderItems";

export default function NewOrder() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", customer_name: "", customer_phone: "", customer_email: "", process_time: "Normal Job", notes: "", source: "walk-in", delivery_method: "" });
  const [items, setItems] = useState([newItem(false)]);
  const [pts, setPts] = useState(PROCESS_TIMES);
  const [products, setProducts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)); loadProcessTimes().then((p) => setPts([...p]));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active !== false)));
    api.get("/delivery-methods").then((r) => { setMethods(r.data); if (r.data[0]) setForm((f) => ({ ...f, delivery_method: r.data[0].name })); });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const ready = items.filter((it) => (it.manual ? it.product.trim() : it.product_id));
    if (!ready.length) return toast.error("Add at least one product");
    if (!quote?.ok) return toast.error(quote?.errors?.[0] || "Complete the product specifications to get a price");
    setSaving(true);
    try {
      const payload = { ...form, items: ready.map(itemPayload), product: ready[0].manual ? ready[0].product : (products.find((p) => p.id === ready[0].product_id)?.name || "Order"), quantity: quote.quantity };
      if (!payload.customer_id) delete payload.customer_id;
      const { data } = await api.post("/jobs", payload);
      toast.success(`Job ${data.job_number} created (${ready.length} item${ready.length > 1 ? "s" : ""})`);
      nav(`/jobs/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create job");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="new-order-page">
      <Button variant="ghost" onClick={() => nav(-1)} className="mb-2"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase">New Custom Order</div>
        <h1 className="text-3xl font-display font-extrabold tracking-tight">Create New Job Card</h1>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <Card className="p-5 space-y-4">
          <h2 className="font-display font-bold">Customer Information</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Existing Customer (optional)</Label>
              <Select value={form.customer_id || "none"} onValueChange={(v) => {
                if (v === "none") { set("customer_id", ""); return; }
                const c = customers.find(x => x.id === v);
                if (c) setForm(f => ({ ...f, customer_id: c.id, customer_name: c.name, customer_phone: c.phone, customer_email: c.email }));
              }}>
                <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select or enter new" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— New customer —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} • {c.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="order-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk-in">Walk-in</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="web_enquiry">Web Enquiry</SelectItem>
                  <SelectItem value="repeat">Repeat Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Customer Name *</Label><Input required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="order-customer-name" /></div>
            <div><Label>Phone</Label><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="order-customer-phone" /></div>
            <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="order-customer-email" /></div>
          </div>
        </Card>

        <Card className="p-5 space-y-4" data-testid="order-product-card">
          <div>
            <h2 className="font-display font-bold">Products</h2>
            <p className="text-xs text-muted-foreground">Add one or more products to this order. Each item can be a catalog product (auto quote) or a manual entry.</p>
          </div>
          <OrderItemsEditor items={items} onChange={setItems} products={products} quoteUrl="/products/quote" processTime={form.process_time} deliveryMethod={form.delivery_method} onQuote={setQuote} allowManual testid="order-item" />
          <div>
            <Label>Delivery / Pickup</Label>
            <DeliveryPicker methods={methods} value={form.delivery_method} onChange={(v) => set("delivery_method", v)} testid="order-delivery" />
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <h2 className="font-display font-bold">Process Time & Notes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Process Time (Excluding Postage Duration) *</Label>
              <Select value={form.process_time} onValueChange={(v) => set("process_time", v)}>
                <SelectTrigger data-testid="order-process-time"><SelectValue /></SelectTrigger>
                <SelectContent>{pts.map((p) => <SelectItem key={p.value} value={p.value}>{p.value} — {p.turnaround}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Deadline is calculated automatically from the process time.</p>
            </div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="order-notes" placeholder="Additional notes..." /></div>
          </div>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
          <Button type="submit" disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="order-submit"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Create Job Card"}</Button>
        </div>
      </form>
    </div>
  );
}
